import { useState } from "react";
import { resolveEventColor, resolveProviderBadge } from "@/lib/eventColor";
import { getJobStatusColor } from "@/lib/staffColors";
import { withAlpha } from "@/lib/colorUtils";
import { getNewDateFromDrag, snapToIncrement } from "@/lib/dragEngine";
import { ResizeHandle } from "./ResizeHandle";
import { QuickPreviewPopover } from "./QuickPreviewPopover";
import { EventDetailsCard } from "./EventDetailsCard";
import { JobStatus } from "../StatusBadge";

interface EventBlockProps {
  id: string;
  title: string;
  jobNumber?: string;
  customer?: string;
  address?: string;
  phone?: string;
  description?: string;
  date: Date;
  endDate?: Date;
  status: JobStatus;
  assignedStaff?: any[];
  top: number;
  height: number;
  conflictColor?: string;
  statusColor?: string;
  jobTypeColor?: string;
  providerColor?: string;
  source?: string;
  staffId?: string;
  highestConflictSeverity?: string;
  onClick?: () => void;
  onMove?: (update: {
    eventId: string;
    newStart: Date;
    newEnd: Date;
    staffId?: string;
  }) => void;
  onOpenJob?: (jobId: string) => void;
  onReassignStaff?: (jobId: string) => void;
  onNavigate?: (address: string) => void;
  onCallCustomer?: (phone: string) => void;
  allStaff?: any[];
  allEvents?: any[];
}

export function EventBlock({
  id,
  title,
  jobNumber,
  customer,
  address,
  phone,
  description,
  date,
  endDate,
  status,
  assignedStaff,
  top,
  height,
  conflictColor,
  statusColor,
  jobTypeColor,
  providerColor,
  source,
  staffId,
  highestConflictSeverity,
  onClick,
  onMove,
  onOpenJob,
  onReassignStaff,
  onNavigate,
  onCallCustomer,
  allStaff,
  allEvents,
}: EventBlockProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [originalTop, setOriginalTop] = useState(top);
  const [tempTop, setTempTop] = useState(top);
  const [pendingMove, setPendingMove] = useState<{ newStart: Date; newEnd: Date } | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Prepare event with status color for unified engine
  const enrichedEvent = {
    id,
    title,
    date,
    status,
    conflictColor,
    statusColor: statusColor || getJobStatusColor(status),
    jobTypeColor,
    providerColor,
    source,
  };

  // Use unified color engine - single source of truth
  const finalColor = resolveEventColor(enrichedEvent);
  const badgeColor = resolveProviderBadge(enrichedEvent);

  // Prepare full event object for popover and modal
  const fullEvent = {
    id,
    title,
    jobNumber,
    customer: customer ?? '',
    address,
    phone,
    description,
    date,
    endDate,
    status,
    assignedStaff,
    conflictColor,
    statusColor,
    jobTypeColor,
    providerColor,
    source,
    highestConflictSeverity,
  };

  function handleClick() {
    if (isDragging) return; // Don't open details if we were dragging
    if (onClick) {
      onClick();
    } else {
      setShowDetails(true);
    }
  }

  function handleDragStart(e: React.DragEvent) {
    if (!onMove) return; // Only allow drag if onMove handler exists
    
    e.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
    setDragStartY(e.clientY);
    setOriginalTop(top);
    setTempTop(top);
    setPendingMove(null);
    
    // Set drag image to be semi-transparent
    if (e.dataTransfer.setDragImage) {
      const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
      dragImage.style.opacity = "0.5";
      e.dataTransfer.setDragImage(dragImage, 0, 0);
    }
  }

  function handleDrag(e: React.DragEvent) {
    if (!isDragging || !onMove) return;
    if (e.clientY === 0) return; // Ignore invalid coordinates during drag
    
    const deltaY = e.clientY - dragStartY;
    
    // Calculate new start time based on vertical movement
    // 60px per hour = 1px per minute
    const newStart = snapToIncrement(
      getNewDateFromDrag(date, deltaY, 1)
    );

    // Preserve event duration
    const eventEnd = endDate || date;
    const duration = eventEnd.getTime() - date.getTime();
    const newEnd = new Date(newStart.getTime() + duration);

    // Update local visual state only (optimistic UI during drag)
    setTempTop(originalTop + deltaY);
    setPendingMove({ newStart, newEnd });
  }

  function handleDragEnd() {
    setIsDragging(false);
    
    // Only persist to backend on drop (not during drag)
    if (pendingMove && onMove) {
      onMove({
        eventId: id,
        newStart: pendingMove.newStart,
        newEnd: pendingMove.newEnd,
        staffId,
      });
    }
    
    // Reset temporary state
    setPendingMove(null);
    setTempTop(top);
  }

  function handleResize(deltaY: number) {
    if (!onMove || !endDate) return;

    // Calculate new end time based on resize movement
    // 60px per hour = 1px per minute
    const newEnd = snapToIncrement(
      getNewDateFromDrag(endDate, deltaY, 1)
    );

    // Ensure new end is after start (minimum 15 minutes)
    const minEnd = new Date(date.getTime() + 15 * 60 * 1000);
    if (newEnd < minEnd) {
      return; // Don't allow events shorter than 15 minutes
    }

    onMove({
      eventId: id,
      newStart: date,
      newEnd,
      staffId,
    });
  }

  return (
    <>
      <div
        draggable={!!onMove}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={`absolute rounded-md p-2 shadow-sm pointer-events-auto transition-all duration-150 ${
          onMove ? "cursor-move hover:shadow-md" : "cursor-pointer"
        } ${isDragging ? "opacity-60 shadow-lg" : ""} ${isHovering ? "ring-1" : ""}`}
        style={{
          top: `${isDragging ? tempTop : top}px`,
          height: `${height}px`,
          left: "4px",
          right: "4px",
          background: withAlpha(finalColor, 0.15),
          borderLeft: `4px solid ${finalColor}`,
          '--tw-ring-color': finalColor,
          borderRadius: "4px",
        } as React.CSSProperties}
        onClick={handleClick}
        data-testid={`event-block-${id}`}
      >
      {/* Provider badge in top-left corner */}
      {source && source !== 'workdeck' && (
        <div 
          className="absolute top-0.5 left-0.5 h-2 w-2 rounded-full ring-1 ring-background"
          style={{ backgroundColor: badgeColor }}
          title={`Source: ${source}`}
        />
      )}

      <div className="font-semibold text-[13px] tracking-tight truncate">{title}</div>
      
      {jobNumber && (
        <div className="text-[10px] text-muted-foreground truncate">{jobNumber}</div>
      )}
      
      {customer && (
        <div className="text-[11px] truncate opacity-80">{customer}</div>
      )}
      
      <div className="text-[11px] font-medium text-foreground/95">
        {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        {endDate && endDate.getTime() !== date.getTime() && (
          <>
            {" — "}
            {endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </>
        )}
        {endDate && endDate.getTime() !== date.getTime() && (() => {
          // Format duration helper
          const formatDuration = (hours: number): string => {
            if (hours < 1) {
              return `${Math.round(hours * 60)}m`;
            } else if (hours === 1) {
              return "1h";
            } else if (hours < 24) {
              const wholeHours = Math.floor(hours);
              const minutes = Math.round((hours - wholeHours) * 60);
              if (minutes === 0) {
                return `${wholeHours}h`;
              }
              return `${wholeHours}h ${minutes}m`;
            } else {
              const days = Math.floor(hours / 24);
              const remainingHours = Math.floor(hours % 24);
              if (remainingHours === 0) {
                return `${days}d`;
              }
              return `${days}d ${remainingHours}h`;
            }
          };
          
          const duration = (endDate.getTime() - date.getTime()) / (1000 * 60 * 60);
          return (
            <span className="text-[10px] opacity-75 ml-1">
              • {formatDuration(duration)}
            </span>
          );
        })()}
      </div>

        {/* Resize handle - only show if onMove handler exists and event has duration */}
        {onMove && endDate && height > 30 && (
          <ResizeHandle onResize={handleResize} />
        )}
      </div>

      {/* Hover Preview - show when hovering and not showing details */}
      {isHovering && !showDetails && !isDragging && (
        <QuickPreviewPopover event={fullEvent} />
      )}

      {/* Full Details Modal - show when clicked */}
      {showDetails && (
        <EventDetailsCard
          event={fullEvent}
          onClose={() => setShowDetails(false)}
          onOpenJob={onOpenJob}
          onReassignStaff={onReassignStaff}
          onNavigate={onNavigate}
          onCallCustomer={onCallCustomer}
          allStaff={allStaff}
          allEvents={allEvents}
        />
      )}
    </>
  );
}
